// Concedii — react-query hooks over `lib/leaveApi`. Shared with the Expo app
// as `@web/hooks/useLeave`, so nothing here touches the DOM.
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import {
  cancelLeaveRequest,
  createLeaveRequest,
  decideLeaveRequest,
  deleteLeaveRequest,
  getLeaveBalance,
  getLeaveReport,
  getPendingLeaveCount,
  listLeaveDays,
  listLeaveRequests,
  listPublicHolidays,
  setLeaveEntitlement,
  updateLeaveRequest,
  type CreateLeaveInput,
  type LeaveFilters,
  type UpdateLeaveInput,
} from '../lib/leaveApi';

/**
 * Every leave query hangs off this prefix, so one write can invalidate the
 * list, the balance, the calendar bands and the pending badge at once —
 * approving a request changes all four.
 */
const ROOT = ['leave'] as const;

function invalidate(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ROOT });
}

export function useLeaveRequests(filters: LeaveFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'requests', filters],
    queryFn: () => listLeaveRequests(filters),
    enabled,
  });
}

export function useLeaveBalance(userId?: string, year?: number, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'balance', userId ?? 'me', year ?? 'current'],
    queryFn: () => getLeaveBalance(userId, year),
    enabled,
    // A 403 means "not yours to see"; asking again changes nothing.
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 1,
  });
}

/**
 * The approved absences inside a window — what the calendar paints. Disabled
 * by default-ish via `enabled` so a page that has not worked out its range
 * yet does not fire a request for an empty one.
 */
export function useLeaveDays(from: string, to: string, userId?: string, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'days', from, to, userId ?? 'all'],
    queryFn: () => listLeaveDays(from, to, userId),
    enabled: enabled && Boolean(from) && Boolean(to),
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 1,
  });
}

/**
 * The legal holiday table. It is fixed for a given year, so it is cached hard
 * — refetching it on every window focus would be pure noise.
 */
export function usePublicHolidays(year: number, through?: number) {
  return useQuery({
    queryKey: [...ROOT, 'holidays', year, through ?? year],
    queryFn: () => listPublicHolidays(year, through),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

/** The manager's queue size, for the sidebar badge. 0 when you cannot approve. */
export function usePendingLeaveCount(enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'pending-count'],
    queryFn: getPendingLeaveCount,
    enabled,
    refetchInterval: 5 * 60 * 1000,
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 1,
  });
}

export function useLeaveReport(year: number, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'report', year],
    queryFn: () => getLeaveReport(year),
    enabled,
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 1,
  });
}

export function useCreateLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeaveInput) => createLeaveRequest(input),
    onSuccess: () => invalidate(qc),
    onError: () => invalidate(qc),
  });
}

export function useUpdateLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateLeaveInput }) =>
      updateLeaveRequest(id, patch),
    onSuccess: () => invalidate(qc),
    onError: () => invalidate(qc),
  });
}

export function useDecideLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: 'approve' | 'reject';
      note?: string | null;
    }) => decideLeaveRequest(id, decision, note),
    onSuccess: () => invalidate(qc),
    onError: () => invalidate(qc),
  });
}

export function useCancelLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelLeaveRequest(id),
    onSuccess: () => invalidate(qc),
    onError: () => invalidate(qc),
  });
}

export function useDeleteLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLeaveRequest(id),
    onSuccess: () => invalidate(qc),
    onError: () => invalidate(qc),
  });
}

export function useSetLeaveEntitlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personId, days }: { personId: string; days: number | null }) =>
      setLeaveEntitlement(personId, days),
    onSuccess: () => invalidate(qc),
  });
}
